import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantNavigation } from '../../hooks/useTenantNavigation';
import { prestamoService } from '../../services/api';
import '../../styles/Clientes.css';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { usePermisosGuard } from '../../hooks/usePermisosGuard';

const ListaPrestamos = () => {
  usePermisosGuard('prestamos', 'ver');
  const [prestamos, setPrestamos] = useState([]);
  const [prestamosFiltrados, setPrestamosFiltrados] = useState([]);
  const [filtro, setFiltro] = useState('todos'); // 'todos', 'activos', 'pagados', 'reenganchados'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showColumnModal, setShowColumnModal] = useState(false);
  const navigate = useNavigate();
  const { getTenantPath, tenantNavigate } = useTenantNavigation();

  useEffect(() => {
    loadPrestamos();
  }, []);

  useEffect(() => {
    aplicarFiltro();
  }, [filtro, prestamos]);

  const loadPrestamos = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await prestamoService.getPrestamos({ per_page: 100 });
      console.log('Préstamos recibidos:', response.data.prestamos);
      
      // Ordenar: activos primero, luego reenganchados/refinanciados al final
      let prestamosOrdenados = (response.data.prestamos || []).sort((a, b) => {
        // Activos primero
        if (a.estado === 'activo' && b.estado !== 'activo') return -1;
        if (a.estado !== 'activo' && b.estado === 'activo') return 1;
        // Reenganchados/refinanciados al final
        if ((a.estado === 'reenganchado' || a.estado === 'refinanciado') && 
            b.estado !== 'reenganchado' && b.estado !== 'refinanciado') return 1;
        if ((b.estado === 'reenganchado' || b.estado === 'refinanciado') && 
            a.estado !== 'reenganchado' && a.estado !== 'refinanciado') return -1;
        // Por fecha descendente
        return new Date(b.fecha_inicio) - new Date(a.fecha_inicio);
      });
      
      setPrestamos(prestamosOrdenados);
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error al cargar los préstamos');
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltro = () => {
    let filtrados = [...prestamos];
    
    switch (filtro) {
      case 'activos':
        filtrados = prestamos.filter(p => p.estado === 'activo');
        break;
      case 'pagados':
        filtrados = prestamos.filter(p => p.estado === 'pagado');
        break;
      case 'reenganchados':
        filtrados = prestamos.filter(p => 
          p.estado === 'reenganchado' || 
          p.estado === 'refinanciado' ||
          p.reemplazado_por !== null
        );
        break;
      case 'todos':
      default:
        filtrados = prestamos;
        break;
    }
    
    setPrestamosFiltrados(filtrados);
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'activo': '#27ae60',      // Verde
      'pagado': '#3498db',      // Azul
      'atrasado': '#f39c12',    // Amarillo
      'vencido': '#e74c3c',     // Rojo
      'reenganchado': '#e67e22', // Naranja
      'refinanciado': '#e67e22', // Naranja
      'cerrado': '#95a5a6'      // Gris
    };
    return colors[estado] || '#95a5a6';
  };

  const exportToExcel = (tipo) => {
    alert('Función de exportar a Excel en desarrollo');
  };

  const copyToClipboard = (tipo) => {
    alert('Función de copiar en desarrollo');
  };

  const printTable = (tipo) => {
    window.print();
  };

  const toggleColumnVisibility = () => {
    setShowColumnModal(!showColumnModal);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-DO');
  };

  const eliminarPrestamoPermamente = async (prestamoId, e) => {
    e.stopPropagation(); // Evitar que se abra el modal de editar

    const confirmacion = window.confirm(
      '⚠️ ¿ELIMINAR PRÉSTAMO PERMANENTEMENTE?\n\n' +
      'Esta acción NO SE PUEDE DESHACER.\n' +
      'Se eliminará el préstamo y todos sus pagos asociados de la base de datos.\n\n' +
      '¿Está seguro de continuar?'
    );

    if (!confirmacion) return;

    try {
      await prestamoService.eliminarPermanente(prestamoId);
      alert('✅ Préstamo eliminado permanentemente');
      loadPrestamos(); // Recargar la lista
    } catch (error) {
      alert('Error al eliminar préstamo: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>Ver Préstamos</h1>
        <div style={{display: 'flex', gap: '1rem'}}>
          <button
            className="btn"
            onClick={() => tenantNavigate('/prestamos/nuevo')}
            style={{background: '#27ae60'}}
          >
            + Nuevo Préstamo
          </button>
          <button
            className="btn"
            onClick={() => tenantNavigate('/prestamos')}
            style={{background: '#666'}}
          >
            ← Volver
          </button>
        </div>
      </div>

      {error && <div className="banner error">{error}</div>}

      {/* Selector de Filtros */}
      <div className="card" style={{marginBottom: '1rem'}}>
        <div style={{padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap'}}>
          <label style={{fontWeight: 'bold', fontSize: '1rem'}}>Filtrar por:</label>
          <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
            <button
              className={`btn ${filtro === 'todos' ? 'active' : ''}`}
              onClick={() => setFiltro('todos')}
              style={{
                background: filtro === 'todos' ? '#3498db' : '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filtro === 'todos' ? 'bold' : 'normal'
              }}
            >
              Todos los Préstamos
            </button>
            <button
              className={`btn ${filtro === 'activos' ? 'active' : ''}`}
              onClick={() => setFiltro('activos')}
              style={{
                background: filtro === 'activos' ? '#27ae60' : '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filtro === 'activos' ? 'bold' : 'normal'
              }}
            >
              Préstamos Activos
            </button>
            <button
              className={`btn ${filtro === 'pagados' ? 'active' : ''}`}
              onClick={() => setFiltro('pagados')}
              style={{
                background: filtro === 'pagados' ? '#3498db' : '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filtro === 'pagados' ? 'bold' : 'normal'
              }}
            >
              Préstamos Pagados
            </button>
            <button
              className={`btn ${filtro === 'reenganchados' ? 'active' : ''}`}
              onClick={() => setFiltro('reenganchados')}
              style={{
                background: filtro === 'reenganchados' ? '#e67e22' : '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filtro === 'reenganchados' ? 'bold' : 'normal'
              }}
            >
              Préstamos Reenganchados
            </button>
          </div>
          <div style={{marginLeft: 'auto', fontSize: '0.9rem', color: '#666'}}>
            Mostrando: <strong>{prestamosFiltrados.length}</strong> de <strong>{prestamos.length}</strong> préstamos
          </div>
        </div>
      </div>

      {/* Lista de Préstamos */}
      <div className="card">
        <div className="card-title" style={{background: '#3498db'}}>
          {filtro === 'todos' && 'Todos los Préstamos'}
          {filtro === 'activos' && 'Préstamos Activos'}
          {filtro === 'pagados' && 'Préstamos Pagados'}
          {filtro === 'reenganchados' && 'Préstamos Reenganchados'}
        </div>

        <div className="table-actions" style={{padding: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center'}}>
          <button className="btn" onClick={() => exportToExcel('todos')}>📄 Excel</button>
          <button className="btn" onClick={() => copyToClipboard('todos')}>📋 Copiar</button>
          <button className="btn" onClick={() => printTable('todos')}>🖨️ Imprimir</button>
          <button className="btn" onClick={toggleColumnVisibility}>👁️ Visibilidad de columnas</button>
          <button 
            className="btn" 
            onClick={() => {
              const prestamoId = prompt('🟨 Historial de Reenganche\n\nIngrese el ID del préstamo para ver su historial:');
              if (prestamoId && !isNaN(prestamoId) && prestamoId.trim() !== '') {
                tenantNavigate(`/prestamos/historial-reenganche?prestamo_id=${prestamoId.trim()}`);
              } else if (prestamoId !== null) {
                alert('Por favor ingrese un ID de préstamo válido.');
              }
            }}
            style={{ 
              background: '#f39c12', 
              color: 'white', 
              fontWeight: 'bold',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.95rem'
            }}
            title="Ver historial completo de reenganches de un préstamo"
          >
            🟨 Historial de Reenganche
          </button>
        </div>

        <p style={{padding: '0 1rem'}}>
          Mostrando registros del 1 al {prestamosFiltrados.length} de un total de {prestamosFiltrados.length} registros
        </p>

        {prestamosFiltrados.length === 0 ? (
          <p style={{padding: '1rem', textAlign: 'center', color: '#666'}}>
            {prestamos.length === 0 
              ? 'No hay préstamos registrados'
              : `No hay préstamos ${filtro === 'activos' ? 'activos' : filtro === 'pagados' ? 'pagados' : filtro === 'reenganchados' ? 'reenganchados' : ''}`
            }
          </p>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Monto</th>
                  <th>Capital</th>
                  <th>Interés</th>
                  <th>Tasa</th>
                  <th>Modalidad</th>
                  <th>Cuotas</th>
                  <th>Pagadas</th>
                  <th>Estado</th>
                  <th>Reemplazado por</th>
                  <th>Fecha Inicio</th>
                  <th>Efectividad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {prestamosFiltrados.map((prestamo) => (
                  <tr
                    key={prestamo.id}
                    onClick={() => tenantNavigate(`/prestamos/editar?id=${prestamo.id}`)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = ''}
                    title="Click para ver detalles del préstamo"
                  >
                    <td>{prestamo.id}</td>
                    <td>{prestamo.cliente?.nombres} {prestamo.cliente?.apellidos}</td>
                    <td><span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      background: prestamo.tipo === 'capital_final' || prestamo.tipo === 'capital_final_fijo' ? '#3498db' :
                                 prestamo.tipo === 'amortizado' || prestamo.tipo === 'amortizado_frances' ? '#27ae60' : '#95a5a6',
                      color: 'white'
                    }}>{prestamo.tipo}</span></td>
                    <td>{formatCurrency(prestamo.total)}</td>
                    <td>{formatCurrency(prestamo.capital)}</td>
                    <td>{formatCurrency(prestamo.interes)}</td>
                    <td>{formatPercent(prestamo.tasa)}</td>
                    <td>{prestamo.modalidad}</td>
                    <td>{prestamo.cuotas}</td>
                    <td>{prestamo.cuotas_pagadas || 0}</td>
                    <td><span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      background: getEstadoColor(prestamo.estado),
                      color: 'white',
                      fontWeight: '500'
                    }}>{prestamo.estado}</span></td>
                    <td>
                      {prestamo.reemplazado_por ? (
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          background: '#3498db',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>#{prestamo.reemplazado_por}</span>
                      ) : (
                        <span style={{ color: '#95a5a6', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td>{formatDate(prestamo.fecha_inicio)}</td>
                    <td>{formatDate(prestamo.fecha_efectividad)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {prestamo.estado === 'cancelado' && (
                        <button
                          onClick={(e) => eliminarPrestamoPermamente(prestamo.id, e)}
                          style={{
                            background: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 'bold'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#c0392b'}
                          onMouseLeave={(e) => e.target.style.background = '#e74c3c'}
                          title="Eliminar préstamo permanentemente de la base de datos"
                        >
                          🗑️ Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaPrestamos;
